import { z } from "zod";
import { LANGUAGES } from "./enums";
import { garageNameInput } from "./schemas/entities";

/**
 * Accounts on a hosted server (ADR 0011): who may do what. Signing in is the identity provider's
 * job; the roles, memberships and rules here are the app's own.
 */

/** Platform roles: an admin runs the admin interface and can open every garage. */
export const PLATFORM_ROLES = ["admin"] as const;
export type PlatformRole = (typeof PLATFORM_ROLES)[number];

/** Roles within one garage: a garage admin also manages its members and garage settings. */
export const GARAGE_ROLES = ["garage_admin", "garage_user"] as const;
export type GarageRole = (typeof GARAGE_ROLES)[number];

/**
 * A disabled user was switched off by an admin; a deleted one was deleted by an admin, keeping
 * only a small record. Neither may use anything (the restricted state).
 */
export const USER_STATUSES = ["active", "disabled", "deleted"] as const;
export type UserStatus = (typeof USER_STATUSES)[number];

/** A deactivated member keeps their place in the garage but can't open it. */
export const MEMBER_STATUSES = ["active", "deactivated"] as const;
export type MemberStatus = (typeof MEMBER_STATUSES)[number];

/** What a request may do in one garage. */
export type GarageAccess =
  /** Single-user installations (desktop app, local development): everything. */
  | { kind: "full" }
  /** A platform admin, member or not. */
  | { kind: "admin" }
  | { kind: "member"; role: GarageRole }
  /** Not a member, a deactivated member, or a user who isn't active. */
  | { kind: "none" };

/** Each garage permission and the garage roles that have it. Later roles and permissions go here. */
export const GARAGE_PERMISSIONS = {
  "garage.use": ["garage_admin", "garage_user"],
  /** Rename the garage, change its garage settings, replace its data with a package. */
  "garage.manage": ["garage_admin"],
  "members.manage": ["garage_admin"],
} as const satisfies Record<string, readonly GarageRole[]>;
export type GaragePermission = keyof typeof GARAGE_PERMISSIONS;

export function can(access: GarageAccess, permission: GaragePermission): boolean {
  switch (access.kind) {
    case "full":
    case "admin":
      return true;
    case "member":
      return (GARAGE_PERMISSIONS[permission] as readonly GarageRole[]).includes(access.role);
    case "none":
      return false;
  }
}

/** A user's access to a garage, from their account and their membership there (if any). */
export function garageAccess(
  user: { status: UserStatus; platformRole: PlatformRole | null },
  membership: { role: GarageRole; status: MemberStatus } | null,
): GarageAccess {
  if (user.status !== "active") return { kind: "none" };
  if (user.platformRole === "admin") return { kind: "admin" };
  if (membership?.status === "active") return { kind: "member", role: membership.role };
  return { kind: "none" };
}

export type MemberChange =
  | { kind: "role"; role: GarageRole }
  | { kind: "deactivate" }
  | { kind: "reactivate" }
  | { kind: "remove" }
  /** The member deletes their own account: the right to erasure comes first. */
  | { kind: "deleteOwnAccount" };

/**
 * Whether a change would leave a garage that has an active garage admin without one. A garage
 * that already has none (its last garage admin deleted their account) isn't blocked further.
 */
export function lastGarageAdminProblem(
  members: readonly { userId: string; role: GarageRole; status: MemberStatus }[],
  userId: string,
  change: MemberChange,
): boolean {
  if (change.kind === "deleteOwnAccount") return false;
  const isActiveAdmin = (m: { role: GarageRole; status: MemberStatus }) =>
    m.role === "garage_admin" && m.status === "active";
  if (!members.some(isActiveAdmin)) return false;
  const after = members.flatMap((m) => {
    if (m.userId !== userId) return [m];
    if (change.kind === "remove") return [];
    if (change.kind === "role") return [{ ...m, role: change.role }];
    const status: MemberStatus = change.kind === "deactivate" ? "deactivated" : "active";
    return [{ ...m, status }];
  });
  return !after.some(isActiveAdmin);
}

export type PlatformAdminChange = "removeRole" | "disable" | "delete" | "deleteOwnAccount";

/** The last active platform admin can't lose the role, be disabled or be deleted: someone has to run the service. */
export function lastPlatformAdminProblem(
  admins: readonly { userId: string; status: UserStatus }[],
  userId: string,
  _change: PlatformAdminChange,
): boolean {
  const active = admins.filter((a) => a.status === "active");
  return active.length === 1 && active[0]?.userId === userId;
}

export const INVITE_DAYS_DEFAULT = 7;
export const INVITE_DAYS_MIN = 1;
export const INVITE_DAYS_MAX = 90;

/** When an invitation made now runs out. */
export function inviteExpiry(now: Date, days: number): Date {
  return new Date(now.getTime() + days * 24 * 60 * 60 * 1000);
}

export type InvitationState = "pending" | "expired" | "accepted" | "revoked";

export function invitationState(
  invitation: { acceptedAt: string | null; revokedAt: string | null; expiresAt: string },
  now: Date,
): InvitationState {
  if (invitation.acceptedAt) return "accepted";
  if (invitation.revokedAt) return "revoked";
  return new Date(invitation.expiresAt).getTime() <= now.getTime() ? "expired" : "pending";
}

/** Long enough is all a password needs to be; no character rules (NIST SP 800-63B). */
export const PASSWORD_MIN = 8;
export const PASSWORD_MAX = 128;

export function passwordProblem(password: string): "tooShort" | "tooLong" | null {
  if (password.length < PASSWORD_MIN) return "tooShort";
  if (password.length > PASSWORD_MAX) return "tooLong";
  return null;
}

export const email = z
  .string()
  .trim()
  .toLowerCase()
  .max(254, { error: "validation.tooLong" })
  .pipe(z.email({ error: "validation.email" }));

/** A new password: checked against the policy before the identity provider sees it. */
export const newPassword = z
  .string()
  .min(PASSWORD_MIN, { error: "validation.passwordTooShort" })
  .max(PASSWORD_MAX, { error: "validation.tooLong" });

/** A password typed to prove who you are; the policy isn't checked again. */
export const currentPassword = z
  .string()
  .min(1, { error: "validation.required" })
  .max(PASSWORD_MAX, { error: "validation.tooLong" });

export const personName = z
  .string()
  .trim()
  .min(1, { error: "validation.required" })
  .max(100, { error: "validation.tooLong" });

/** One-time tokens from email links. */
export const linkToken = z.string().trim().min(1).max(200);

export const signInInput = z.object({ email, password: currentPassword });
export const profileInput = z.object({ firstName: personName, lastName: personName });
export type ProfileInput = z.infer<typeof profileInput>;
export const changePasswordInput = z.object({ current: currentPassword, next: newPassword });
export const requestPasswordResetInput = z.object({ email, language: z.enum(LANGUAGES) });
export const resetPasswordInput = z.object({ token: linkToken, password: newPassword });
export const deleteAccountInput = z.object({ password: currentPassword });

export const inviteInput = z.object({
  email,
  firstName: personName,
  lastName: personName,
  role: z.enum(GARAGE_ROLES),
});
export type InviteInput = z.infer<typeof inviteInput>;

/**
 * Accepting an invitation: a new account chooses a password; an existing account signs in with
 * its password, or needs none when it is already signed in. The names are the invitation's,
 * possibly corrected; an existing active account keeps its own.
 */
export const acceptInvitationInput = z.object({
  token: linkToken,
  firstName: personName,
  lastName: personName,
  password: z.string().max(PASSWORD_MAX, { error: "validation.tooLong" }).nullish(),
});

/** A new garage with its first users, at least one of them a garage admin. */
export const adminCreateGarageInput = z.object({
  name: garageNameInput.shape.name,
  users: z
    .array(inviteInput)
    .min(1, { error: "validation.garageAdminRequired" })
    .max(20)
    .refine((users) => users.some((u) => u.role === "garage_admin"), {
      error: "validation.garageAdminRequired",
    })
    .refine((users) => new Set(users.map((u) => u.email)).size === users.length, {
      error: "validation.emailTwice",
    }),
  language: z.enum(LANGUAGES),
});
export type AdminCreateGarageInput = z.infer<typeof adminCreateGarageInput>;

export const platformSettingsInput = z.object({
  inviteDays: z
    .int({ error: "validation.number" })
    .min(INVITE_DAYS_MIN, { error: "validation.inviteDays" })
    .max(INVITE_DAYS_MAX, { error: "validation.inviteDays" }),
});
export type PlatformSettings = z.infer<typeof platformSettingsInput>;
